<script setup>
import { ref, watch } from 'vue';
import { showLogConsole } from '@/commons/utils';
import { useArloService } from '@/services/arloapi';
import { basicosEndpoint } from '@/api/endpoints/basicos';

const { getDados } = useArloService();

const props = defineProps({
    modelValue: {
        type: [Object, String, Number],
        default: null
    },
    cboDescricao: {
        type: String,
        default: null
    },
    cboId: {
        type: [String, Number],
        default: null
    },
    error: {
        type: String,
        default: null
    },
    label: {
        type: String,
        default: 'CBO'
    },
    placeholder: {
        type: String,
        default: 'Digite para buscar uma ocupação'
    },
    obrigatorio: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const cboSelecionado = ref(null);
const listaCbos = ref([]);
const carregandoCbos = ref(false);

// Observar mudanças no modelValue para sincronizar (útil na edição)
watch(() => props.modelValue, (newValue) => {
    if (newValue && typeof newValue === 'object') {
        cboSelecionado.value = newValue;
    } else if (newValue === null) {
        cboSelecionado.value = null;
    }
}, { immediate: true });

// Carregar CBO quando cboDescricao é fornecido (útil na edição)
watch(() => props.cboDescricao, async (newCboDescricao) => {
    if (newCboDescricao && props.cboId && !cboSelecionado.value) {
        try {
            const response = await getDados(`${basicosEndpoint.ocupacaoCbo.listar}?filtro=${encodeURIComponent(newCboDescricao)}`);
            if (response.value && Array.isArray(response.value)) {
                // Busca o CBO específico pelo ID na lista retornada
                const cboEncontrado = response.value.find(cbo => cbo.id === Number(props.cboId));
                if (cboEncontrado) {
                    cboSelecionado.value = cboEncontrado;
                    emit('update:modelValue', cboEncontrado);
                    emit('change', cboEncontrado);
                }
            }
        } catch (error) {
            showLogConsole('Erro ao carregar CBO:', error);
        }
    }
}, { immediate: true });

/**
 * Busca ocupações CBO com base no filtro digitado
 */
const buscarCbos = async (event) => {
    const filtro = event.query;
    
    try {
        const response = await getDados(basicosEndpoint.ocupacaoCbo.listar + '?filtro=' + encodeURIComponent(filtro.trim()));
        listaCbos.value = response.value || [];
    } catch (error) {
        showLogConsole('Erro ao buscar CBOs:', error);
        listaCbos.value = [];
    } 
};

const handleItemSelect = (event) => {
    // Emite quando um item é selecionado da lista
    if (event.value && typeof event.value === 'object') {
        emit('update:modelValue', event.value);
        emit('change', event.value);
    }
};

const handleClear = () => {
    // Emite quando o campo é limpo
    cboSelecionado.value = null;
    emit('update:modelValue', null);
    emit('change', null);
};
</script>

<template>
    <div class="field mb-2 col-12">
        <SmallTitle :titulo="label" :obrigatorio="obrigatorio" />
        <AutoComplete
            v-model="cboSelecionado"
            :suggestions="listaCbos"
            forceSelection
            optionLabel="descricao"
            :placeholder="placeholder"
            emptySearchMessage="Resultados não encontrados"
            class="text-sm w-full"
            :class="{ 'p-invalid': error }"
            :loading="carregandoCbos"
            :disabled="carregandoCbos"
            @complete="buscarCbos"
            @item-select="handleItemSelect"
            @clear="handleClear"
            :pt="{
                input: { class: 'text-sm w-full' },
                panel: { class: 'text-sm', style: 'max-width: 300px' },
                option: { style: 'max-width: 300px;' },
                emptyMessage: { class: 'text-sm pl-2' }
            }">
        </AutoComplete>
        <SmallError :error="error" />
    </div>
</template>
